export const FLOW_STEP_EVENT = "flow_step";

const FLOW_STEP_NAMES = [
  "email",
  "contact_info",
  "education",
  "interest",
  "start_date",
  "phone_verify",
  "address",
  "school_match",
  "final_submit",
  "question",
  "interstitial",
] as const;

export type FlowStepName = (typeof FLOW_STEP_NAMES)[number];

export interface FlowStepAnswerInput {
  fieldKey: string;
  value: unknown;
  options: readonly { value: unknown }[];
}

export interface FlowStepDescriptor {
  stepNumber: number;
  stepName: FlowStepName;
  answer?: FlowStepAnswerInput;
}

export interface FlowStepInput extends FlowStepDescriptor {
  flowName: string;
  uid?: string;
  transactionId?: string;
}

export interface FlowStepDataLayerEvent {
  event: typeof FLOW_STEP_EVENT;
  _clear: true;
  flow_step: {
    flow_name: string;
    step_number: number;
    step_name: FlowStepName;
    uid?: string;
    step_answer?: string;
    transaction_id?: string;
  };
}

interface FlowStepQuestionDefinition {
  stepName: FlowStepName;
  allowAnswer: boolean;
}

// One explicit contract controls both screen semantics and answer privacy.
// CMS mappings and question-key prefixes never grant permission to send an answer.
const QUESTION_FIELDS: Readonly<Record<string, FlowStepQuestionDefinition>> = {
  EDUCATION_LEVEL: { stepName: "education", allowAnswer: true },
  GA_EDUCATION_LEVEL: { stepName: "education", allowAnswer: true },
  SHORTER_EDUCATION_LEVEL: { stepName: "education", allowAnswer: true },
  FB_PSYCHOLOGY_EDUCATION_LEVEL: { stepName: "education", allowAnswer: true },
  INDUSTRY_INTEREST: { stepName: "interest", allowAnswer: true },
  FB_PSYCHOLOGY_INDUSTRY_INTEREST: { stepName: "interest", allowAnswer: true },
  PROGRAM_INTEREST: { stepName: "interest", allowAnswer: true },
  SUBINDUSTRY_INTEREST: { stepName: "interest", allowAnswer: true },
  CONCENTRATION: { stepName: "interest", allowAnswer: true },
  CONCENTRATION_HEALTH: { stepName: "interest", allowAnswer: true },
  // Audited CMS concentration keys; none are approved by prefix or filter mapping.
  CONCENTRATION_HEALTH_IND: { stepName: "interest", allowAnswer: true },
  CONCENTRATION_VOCATION: { stepName: "interest", allowAnswer: true },
  CONCENTRATION_TRADE: { stepName: "interest", allowAnswer: true },
  CONCENTRATION_BUSINESS: { stepName: "interest", allowAnswer: true },
  CONCENTRATION_TECH: { stepName: "interest", allowAnswer: true },
  CONCENTRATION_TRADE_IND: { stepName: "interest", allowAnswer: true },
  CONCENTRATION_BUSINESS_IND: { stepName: "interest", allowAnswer: true },
  CONCENTRATION_TECH_IND: { stepName: "interest", allowAnswer: true },
  CONCENTRATION_TRADE_ALL: { stepName: "interest", allowAnswer: true },
  CONCENTRATION_BUSINESS_ALL: { stepName: "interest", allowAnswer: true },
  CONCENTRATION_TECH_ALL: { stepName: "interest", allowAnswer: true },
  CONCENTRATION_HEALTH_ALL: { stepName: "interest", allowAnswer: true },
  CONCENTRATION_PSYCHOLOGY: { stepName: "interest", allowAnswer: true },
  CONCENTRATION_CRIMINAL_JUSTICE: { stepName: "interest", allowAnswer: true },
  GOOGLE_ADS_WAVE_A_CONCENTRATION: { stepName: "interest", allowAnswer: true },
  FB_PSYCHOLOGY_SPECIALIZATION: { stepName: "interest", allowAnswer: true },
  SNHU_HEALTHCARE_CONCENTRATION: { stepName: "interest", allowAnswer: true },
  SNHU_CRIMINAL_CONCENTRATION: { stepName: "interest", allowAnswer: true },
  UMA_HEALTH_CONCENTRATION: { stepName: "interest", allowAnswer: true },
  CYBERSECURITY_INTERESTED: { stepName: "interest", allowAnswer: true },
  DEGREE: { stepName: "interest", allowAnswer: true },
  DEGREE_ALL: { stepName: "interest", allowAnswer: true },
  DEGREE_MBC: { stepName: "interest", allowAnswer: true },
  NEW_DEGREE: { stepName: "interest", allowAnswer: true },
  FB_PSYCHOLOGY_DEGREE_ALL: { stepName: "interest", allowAnswer: true },
  START_TIMEFRAME: { stepName: "start_date", allowAnswer: true },
  TIMEFRAME_PREFERENCE: { stepName: "start_date", allowAnswer: true },
  GA_TIMEFRAME_PREFERENCE: { stepName: "start_date", allowAnswer: true },
  YSMDUPE_TIMEFRAME_PREFERENCE: { stepName: "start_date", allowAnswer: true },
  EMAIL: { stepName: "email", allowAnswer: false },
  FIRST_NAME: { stepName: "contact_info", allowAnswer: false },
  LAST_NAME: { stepName: "contact_info", allowAnswer: false },
  NAME_FIRST: { stepName: "contact_info", allowAnswer: false },
  NAME_LAST: { stepName: "contact_info", allowAnswer: false },
  PHONE: { stepName: "contact_info", allowAnswer: false },
  ZIP_CODE: { stepName: "address", allowAnswer: false },
  GA_ZIPCODE: { stepName: "address", allowAnswer: false },
  YSMDUPE_ZIPCODE: { stepName: "address", allowAnswer: false },
  ADDRESS_ZIP: { stepName: "address", allowAnswer: false },
  ADDRESS_ZIP_ONLY: { stepName: "address", allowAnswer: false },
  ADDRESS: { stepName: "address", allowAnswer: false },
};

export function getFlowStepQuestionDefinition(fieldKey: string): FlowStepQuestionDefinition | undefined {
  return Object.hasOwn(QUESTION_FIELDS, fieldKey) ? QUESTION_FIELDS[fieldKey] : undefined;
}

/** Serialize only selected predefined option IDs from approved question fields. */
export function buildFlowStepAnswer(answer?: FlowStepAnswerInput): string | undefined {
  if (!answer || !getFlowStepQuestionDefinition(answer.fieldKey)?.allowAnswer) return;
  const values = Array.isArray(answer.value) ? answer.value : [answer.value];
  if (values.length === 0 || values.length > 20) return;
  const options = new Set(answer.options.map((option) => option.value));
  if (
    !values.every(
      (value): value is string =>
        typeof value === "string" &&
        options.has(value) &&
        // No labels, URLs, email addresses, phone numbers, or arbitrary prose.
        (/^[a-z][a-z0-9_-]{0,99}$/i.test(value) ||
          /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(value))
    )
  ) {
    return;
  }
  const normalized = [...new Set(values.map((value) => value.toLowerCase()))].sort().join("|");
  return normalized.length <= 100 ? normalized : undefined;
}

/** Construct a fresh namespace so optional fields cannot leak from the prior step. */
export function buildFlowStepEvent(input: FlowStepInput): FlowStepDataLayerEvent | undefined {
  if (
    !/^[a-z0-9][a-z0-9_-]{0,99}$/i.test(input.flowName) ||
    !Number.isSafeInteger(input.stepNumber) ||
    input.stepNumber < 1 ||
    !(FLOW_STEP_NAMES as readonly string[]).includes(input.stepName)
  ) {
    return;
  }
  const answer = buildFlowStepAnswer(input.answer);
  const uid = typeof input.uid === "string" && /^\d{1,20}$/.test(input.uid) ? input.uid : undefined;
  const transactionId =
    typeof input.transactionId === "string" && /^[a-z0-9_-]{1,100}$/i.test(input.transactionId)
      ? input.transactionId
      : undefined;
  return {
    event: FLOW_STEP_EVENT,
    _clear: true,
    flow_step: {
      flow_name: input.flowName,
      step_number: input.stepNumber,
      step_name: input.stepName,
      ...(uid ? { uid } : {}),
      ...(answer ? { step_answer: answer } : {}),
      ...(transactionId ? { transaction_id: transactionId } : {}),
    },
  };
}
